import React, { useRef, useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import "./FeatureSteps.css";

const STEP_DURATION = 3000; // ms per step

/**
 * FeatureSteps – auto-gliding step-by-step feature showcase.
 * Desktop: auto-advances every 3 s with a smooth progress bar; click to jump.
 * Mobile (≤768px): simple stacked layout with tap-to-expand.
 */
export function FeatureSteps({
  features,
  className = "",
  title = "How to get Started",
  subtitle,
}) {
  const [isMobile, setIsMobile] = useState(false);
  const [mobileActiveStep, setMobileActiveStep] = useState(0);
  const [currentFeature, setCurrentFeature] = useState(0);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const pausedRef = useRef(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* Auto-advance timer (desktop) */
  const tick = useCallback(() => {
    if (pausedRef.current) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }
    if (!startRef.current) startRef.current = performance.now();
    const elapsed = performance.now() - startRef.current;
    const pct = Math.min((elapsed / STEP_DURATION) * 100, 100);
    setProgress(pct);

    if (elapsed >= STEP_DURATION) {
      setCurrentFeature((prev) => (prev + 1) % features.length);
      startRef.current = performance.now();
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [features.length]);

  useEffect(() => {
    if (isMobile) return;
    startRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [isMobile, tick]);

  /* Pause when section is not visible */
  const sectionRef = useRef(null);
  useEffect(() => {
    if (isMobile || !sectionRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        const wasPaused = pausedRef.current;
        pausedRef.current = !entry.isIntersecting;
        // Reset timer when un-pausing so the step doesn't jump immediately
        // (prevents layout shift that disrupts smooth-scroll navigation)
        if (wasPaused && entry.isIntersecting) {
          startRef.current = performance.now();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, [isMobile]);

  const handleStepClick = (index) => {
    if (isMobile) {
      setMobileActiveStep(index);
      return;
    }
    setCurrentFeature(index);
    setProgress(0);
    startRef.current = performance.now();
  };

  /* ------- MOBILE LAYOUT ------- */
  if (isMobile) {
    return (
      <div className={`feature-steps feature-steps--mobile ${className}`}>
        <div className="feature-steps__container">
          <div className="feature-steps__header">
            <h2 className="feature-steps__title">{title}</h2>
            {subtitle && (
              <p className="feature-steps__subtitle">{subtitle}</p>
            )}
          </div>

          <div className="feature-steps__mobile-list">
            {features.map((feature, index) => {
              const isActive = index === mobileActiveStep;
              const isCompleted = index < mobileActiveStep;

              return (
                <div
                  key={index}
                  className={`feature-steps__mobile-step ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
                  onClick={() => handleStepClick(index)}
                >
                  {/* Step header row */}
                  <div className="feature-steps__mobile-header">
                    <div
                      className={`feature-steps__circle ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
                    >
                      {isCompleted ? (
                        <span className="feature-steps__check">✓</span>
                      ) : (
                        <span className="feature-steps__num">{index + 1}</span>
                      )}
                    </div>
                    <div className="feature-steps__mobile-title-group">
                      <span className="feature-steps__label">{feature.step}</span>
                      <h3 className="feature-steps__step-title">
                        {feature.title || feature.step}
                      </h3>
                    </div>
                  </div>

                  {/* Expandable content */}
                  <AnimatePresence>
                    {isActive && (
                      <motion.div
                        className="feature-steps__mobile-body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: "easeInOut" }}
                      >
                        <p className="feature-steps__desc">{feature.content}</p>
                        <div className="feature-steps__mobile-img">
                          <img
                            src={feature.image}
                            alt={feature.title || feature.step}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  /* ------- DESKTOP LAYOUT ------- */
  return (
    <div
      ref={sectionRef}
      className={`feature-steps feature-steps--auto ${className}`}
    >
      <div className="feature-steps__container">
        {/* Header */}
        <div className="feature-steps__header">
          <h2 className="feature-steps__title">{title}</h2>
          {subtitle && (
            <p className="feature-steps__subtitle">{subtitle}</p>
          )}
        </div>

        <div className="feature-steps__grid">
          {/* Left: step list */}
          <div className="feature-steps__list">
            {features.map((feature, index) => {
              const isActive = index === currentFeature;
              const isCompleted = index < currentFeature;

                return (
                  <div
                    key={index}
                    className={`feature-steps__step ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
                    onClick={() => handleStepClick(index)}
                    role="button"
                    tabIndex={0}
                    style={{
                      opacity: isActive ? 1 : isCompleted ? 0.85 : 0.4,
                    }}
                  >
                    {/* Circle + progress bar column */}
                    <div className="feature-steps__indicator">
                      <div
                        className={`feature-steps__circle ${
                          isActive ? "active" : ""
                        } ${isCompleted ? "completed" : ""}`}
                      >
                        {isCompleted ? (
                          <span className="feature-steps__check">✓</span>
                        ) : (
                          <span className="feature-steps__num">{index + 1}</span>
                        )}
                      </div>

                      {/* Connector with fill */}
                      {index < features.length - 1 && (
                        <div className="feature-steps__connector-track">
                          <div
                            className="feature-steps__connector-fill"
                            style={{
                              height: isCompleted
                                ? "100%"
                                : isActive
                                ? `${progress}%`
                                : "0%",
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Step text */}
                    <div className="feature-steps__text">
                      <span className="feature-steps__label">
                        {feature.step}
                      </span>
                      <h3 className="feature-steps__step-title">
                        {feature.title || feature.step}
                      </h3>
                      <AnimatePresence>
                        {isActive && (
                          <motion.p
                            className="feature-steps__desc"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.35, ease: "easeInOut" }}
                          >
                            {feature.content}
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right: image showcase */}
            <div className="feature-steps__image-panel">
              <AnimatePresence mode="wait">
                {features.map(
                  (feature, index) =>
                    index === currentFeature && (
                      <motion.div
                        key={index}
                        className="feature-steps__image-wrapper"
                        initial={{ y: 60, opacity: 0, scale: 0.96 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: -60, opacity: 0, scale: 0.96 }}
                        transition={{ duration: 0.45, ease: "easeInOut" }}
                      >
                        <img
                          src={feature.image}
                          alt={feature.title || feature.step}
                          className="feature-steps__image"
                        />
                        <div className="feature-steps__image-fade" />
                      </motion.div>
                    )
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
    </div>
  );
}
